import { Link, useNavigate } from "react-router-dom";
import { SheetClose } from "@/components/ui/sheet";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import logger from "@/utils/logger";

export default function MiniAuthOptions({ isSignedIn = false }) {
  const navigate = useNavigate();

  const handlePlanClick = (plan) => {
    logger.log(`MiniAuthOptions: Selected plan ${plan}`);
    // Redirect to signup page with plan query parameter
    navigate(`/signup?plan=${plan}`);
  };

  // Signed in: no sign-up plan cards, just a way back to their tips
  if (isSignedIn) {
    return (
      <div className="space-y-3">
        <hr />
        <SheetClose asChild>
          <Link
            to="/todays-tips"
            className="w-full h-10 bg-brand-pink text-white rounded-lg hover:bg-[#e391c4] transition-colors font-semibold text-sm flex items-center justify-center"
            data-gtm-element-id="navbar_mytips_button-1"
          >
            My tips
          </Link>
        </SheetClose>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <hr />
      <div className="space-y-2 pt-1">
        <div>
          <h3 className="text-xs font-bold text-slate-900 mb-0.5">Choose Your Plan</h3>
          <p className="text-[10px] text-slate-600">Pick the option that works best for you</p>
        </div>

        {/* Plan Selection Cards - Copied and adapted from AuthModal */}
        <div className="space-y-1.5">
          {/* Free Plan */}
          <SheetClose asChild>
            <button
              type="button"
              onClick={() => handlePlanClick("free")}
              className={cn(
                "w-full px-2 py-3 border-2 rounded-md transition-all duration-200 text-left",
                "hover:border-pink-300 focus:outline-none focus:ring-1 focus:ring-pink-200",
                "border-gray-200 bg-white"
              )}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-900 leading-tight">Lexi Social</p>
                  <p className="pt-2 text-[10px] text-slate-600 leading-tight">2 Race-Day tips • All AU races • Full results history</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold text-pink-600">FREE</p>
                </div>
              </div>
            </button>
          </SheetClose>

          {/* 7-Day Pass */}
          <SheetClose asChild>
            <button
              type="button"
              onClick={() => handlePlanClick("day-pass")}
              className={cn(
                "w-full px-2 py-3 border-2 rounded-md transition-all duration-200 text-left",
                "hover:border-pink-300 focus:outline-none focus:ring-1 focus:ring-pink-200",
                "border-gray-200 bg-white"
              )}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-900 leading-tight">Lexi 7-Day Pass</p>
                  <p className="pt-2 text-[10px] text-slate-600 leading-tight">Lexi Pro full access pass for 7 days</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold text-pink-600">$9.00</p>
                  <p className="text-[10px] text-slate-500 leading-tight">once-off</p>
                </div>
              </div>
            </button>
          </SheetClose>

          {/* Pro Plan */}
          <SheetClose asChild>
            <button
              type="button"
              onClick={() => handlePlanClick("pro")}
              className={cn(
                "w-full px-2 py-3 border-2 rounded-md transition-all duration-200 text-left",
                "hover:border-pink-300 outline-none ring-1 ring-pink-200",
                "border-pink-300 bg-white"
              )}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-1">
                    <p className="text-xs font-semibold text-slate-900 leading-tight">Lexi Pro</p>
                    <span className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-pink-600 text-white text-[9px] font-medium rounded-full">
                      <Check className="w-2 h-2" /> Popular
                    </span>
                  </div>
                  <p className="pt-2 text-[10px] text-slate-600 leading-tight">4 Early Mail / Race-Day / Late Mail tips • Lexi Chat • History</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold text-pink-600">$29.95</p>
                  <p className="text-[10px] text-slate-500 leading-tight">/month</p>
                </div>
              </div>
            </button>
          </SheetClose>
        </div>
      </div>

      {/* Separator */}
      <div className="relative py-2">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200" />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="px-2 bg-white text-gray-500">or</span>
        </div>
      </div>

      {/* Sign In Button */}
      <SheetClose asChild>
        <Link
          to="/todays-tips"
          className="w-full h-10 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm flex items-center justify-center"
          data-gtm-element-id="navbar_signin_button-1"
        >
          Sign In
        </Link>
      </SheetClose>

      {/* reCAPTCHA label */}
      <p className="text-[10px] text-gray-500 text-center leading-normal pt-1">
        This site is protected by reCAPTCHA and the <br/>Google{" "}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-pink hover:underline">
          Privacy Policy
        </a>{" "}
        and{" "}
        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-brand-pink hover:underline">
          Terms of Service
        </a>{" "}
        apply.
      </p>
    </div>
  );
}


